import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, ShieldOff, Filter, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";

const DECISION_STYLES = {
  allowed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
};
const DECISION_ICONS = {
  allowed: ShieldCheck,
  blocked: ShieldOff,
  partial: ShieldAlert,
};

export default function AdminCompliance() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [decision, setDecision] = useState("any");
  const [sourceType, setSourceType] = useState("any");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = () => {
    api.get("/admin/compliant-market-insights/status").then(({ data }) => setStatus(data)).catch(() => {});
  };

  useEffect(() => { loadStatus(); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (decision !== "any") params.decision = decision;
    if (sourceType !== "any") params.source_type = sourceType;
    api.get("/admin/compliance-logs", { params })
      .then(({ data }) => { setRows(data.rows || []); setTotals(data.totals || {}); })
      .finally(() => setLoading(false));
  }, [decision, sourceType]);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/compliant-market-insights/refresh");
      if (data?.status === "refreshed") toast.success("Compliant insights refreshed.");
      else if (data?.status === "skipped") toast.info(`Skipped — ${data.reason}.`);
      else toast.info("Refresh acknowledged.");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 504) toast.warning("Sources are slow — refresh continues in the background. Watch the status card.");
      else if (code === 502) toast.error(`Sources unreachable — ${e?.response?.data?.detail || "no_source_reachable"}.`);
      else toast.error(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
      // Give the background task a beat, then re-read status
      setTimeout(loadStatus, 2000);
    }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">SYSTEM · AUDIT</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Compliance <span className="font-serif-italic text-sg-gold-deep">Logs.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Every builder-URL fetch, WhatsApp ingest, and OCR upload is recorded here. Blocked entries include the source robots.txt / ToS reason so you can defend the decision to auditors.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <StatCard label="Allowed" value={totals.allowed || 0} tone="emerald" testId="compliance-total-allowed" />
        <StatCard label="Blocked" value={totals.blocked || 0} tone="red" testId="compliance-total-blocked" />
        <StatCard label="Partial" value={totals.partial || 0} tone="amber" testId="compliance-total-partial" />
      </div>

      <div className="mt-8 bg-white border border-sg-line rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="cron-status-card">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="rounded-full p-3 border border-sg-line bg-sg-cream-soft text-sg-navy shrink-0">
            <Clock className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="overline text-sg-gold-deep">WEEKLY CRON · COMPLIANT MARKET INSIGHTS</div>
            <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-sg-mute">
              <span>Cadence · <span className="text-sg-navy">every {status?.min_interval_days || 6} days</span></span>
              {status?.last_cron_success_at ? (
                <span data-testid="cron-last-success">Last success · <span className="text-sg-navy">{new Date(status.last_cron_success_at).toLocaleString()}</span></span>
              ) : (
                <span data-testid="cron-never-succeeded" className="text-amber-600">Never succeeded yet</span>
              )}
              {status?.last_cron_attempt_at && (
                <span data-testid="cron-last-attempt">Last attempt · <span className="text-sg-navy">{new Date(status.last_cron_attempt_at).toLocaleString()}</span></span>
              )}
              {status?.last_cron_actor && (
                <span>Actor · <span className="text-sg-navy">{status.last_cron_actor}</span></span>
              )}
              {status?.next_scheduled_run_at && (
                <span data-testid="cron-next-run">Next scheduled · <span className="text-sg-navy">{new Date(status.next_scheduled_run_at).toLocaleString()}</span></span>
              )}
            </div>
            {status?.last_cron_error && (
              <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="cron-last-error">
                <span className="font-semibold">Last error</span> · {status.last_cron_error}
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={triggerRefresh}
          disabled={refreshing}
          className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-11 px-5 shrink-0"
          data-testid="cron-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 text-sg-gold ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh Now"}
        </Button>
      </div>

      <div className="mt-8 bg-white border border-sg-line rounded-3xl p-5 flex flex-wrap items-center gap-3">
        <span className="overline text-sg-mute inline-flex items-center gap-2"><Filter className="w-3 h-3" /> FILTER</span>
        <Select value={decision} onValueChange={setDecision}>
          <SelectTrigger className="w-44 rounded-full border-sg-line" data-testid="compliance-filter-decision">
            <SelectValue placeholder="Any decision" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any decision</SelectItem>
            <SelectItem value="allowed">Allowed</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceType} onValueChange={setSourceType}>
          <SelectTrigger className="w-52 rounded-full border-sg-line" data-testid="compliance-filter-source">
            <SelectValue placeholder="Any source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any source</SelectItem>
            <SelectItem value="builder_url">Builder URL</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="ocr">OCR</SelectItem>
            <SelectItem value="magic_drop">Magic Drop</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-sm text-sg-mute font-mono" data-testid="compliance-row-count">
          {loading ? "Loading…" : `${rows.length} entries`}
        </span>
      </div>

      <div className="mt-6 space-y-3">
        {rows.length === 0 && !loading && (
          <div className="text-sm text-sg-mute py-12 text-center border border-dashed border-sg-line rounded-3xl" data-testid="compliance-empty">
            No compliance events match this filter yet.
          </div>
        )}
        {rows.map((r) => {
          const Icon = DECISION_ICONS[r.decision] || ShieldCheck;
          return (
            <div
              key={r.log_id}
              className="bg-white border border-sg-line rounded-2xl p-5 flex flex-col md:flex-row md:items-start gap-4"
              data-testid={`compliance-row-${r.log_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 border ${DECISION_STYLES[r.decision] || DECISION_STYLES.allowed}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline text-sg-mute">{r.source_type}</div>
                  <div className="font-mono text-xs text-sg-navy">{new Date(r.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`rounded-full border ${DECISION_STYLES[r.decision] || DECISION_STYLES.allowed}`}>{r.decision.toUpperCase()}</Badge>
                  <span className="text-sm font-medium text-sg-navy">{r.reason}</span>
                </div>
                {r.url && (
                  <div className="mt-1 text-xs text-sg-mute font-mono truncate" title={r.url}>
                    {r.url}
                  </div>
                )}
                {r.extra?.robots_head && (
                  <details className="mt-2 text-xs text-sg-mute">
                    <summary className="cursor-pointer text-sg-gold-deep">robots.txt excerpt</summary>
                    <pre className="mt-2 whitespace-pre-wrap bg-sg-cream-soft border border-sg-line rounded-lg p-3 max-h-40 overflow-auto">{r.extra.robots_head}</pre>
                  </details>
                )}
                {r.extra?.inserted_count !== undefined && (
                  <div className="text-xs text-sg-mute mt-1">Inserted {r.extra.inserted_count} listing(s)</div>
                )}
              </div>
              <div className="text-xs text-sg-mute font-mono md:text-right">
                <div>Actor</div>
                <div className="text-sg-navy" data-testid={`compliance-row-user-${r.log_id}`}>{r.user_id}</div>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardShell>
  );
}

function StatCard({ label, value, tone, testId }) {
  const toneMap = {
    emerald: "text-emerald-700 border-emerald-200 bg-emerald-50",
    red: "text-red-700 border-red-200 bg-red-50",
    amber: "text-amber-700 border-amber-200 bg-amber-50",
  };
  return (
    <div className={`rounded-3xl p-5 border ${toneMap[tone] || "bg-white border-sg-line"}`} data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="rupee text-4xl mt-2">{value}</div>
    </div>
  );
}
